import {
    IProps,
} from './types';

import classNames from 'classnames';
import React, {
    FC,
    useRef,
    useState,
} from 'react';
import {
    useNavigate,
} from 'react-router-dom';

import {
    useClickOutside,
} from 'tools/hooks';

import {
    useIsDesktop,
    useManualAdaptiveMode,
} from 'helpers/adaptive';

import {
    useAuthByUser,
} from 'entities/auth/hooks';
import {
    PERMISSION,
    ROLE,
} from 'entities/users/constants';
import {
    hasPermissionOrAdmin,
    hasPermissionsOrAdmin,
} from 'entities/users/functions';
import {
    useUserByMe,
} from 'entities/users/hooks';

import {
    PATH,
} from 'pages/constants';

import NavItem from '../Navigation/NavItem';

import {
    formatManualAdaptiveMode,
} from './functions';

import arrowImage from './media/arrow.svg';
import personImage from './media/person.svg';

import styles from './AuthInfo.module.scss';

const AuthInfo: FC<IProps> = (props) => {
    const [isOpen, setIsOpen] = useState(false);

    const {authUser} = useUserByMe();
    const {postAuthLogout} = useAuthByUser();

    const {
        availableManualAdaptiveMode,
        switchManualAdaptiveMode,
    } = useManualAdaptiveMode();

    const navigate = useNavigate();

    const isDesktop = useIsDesktop();

    const containerRef = useRef<HTMLDivElement>(null);

    const onLogout = async () => {
        await postAuthLogout();
        window.location.replace(PATH.SIGN_IN);
    };

    useClickOutside(containerRef, () => {
        setIsOpen(false);
    });

    if (!authUser) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={
                classNames(styles.authInfo, props.className, {
                    [styles.isOpened]: isOpen,
                })
            }
            onClick={() => setIsOpen(!isOpen)}
        >
            <img
                className={styles.personImage}
                src={personImage}
                alt={''}
            />
            {authUser.username}
            <img
                className={styles.arrowImage}
                src={arrowImage}
                alt={''}
            />
            {
                isOpen &&
                <div className={styles.options}>
                    <div
                        className={styles.option}
                        onClick={() => navigate(PATH.SETTINGS)}
                    >
                        Settings
                    </div>
                    <div className={styles.line}/>
                    <div
                        className={styles.option}
                        style={{
                            display: 'none',
                        }}
                        onClick={switchManualAdaptiveMode}
                    >
                        {formatManualAdaptiveMode(availableManualAdaptiveMode)}
                    </div>
                    {
                        isDesktop &&
                        <div className={styles.navigation}>
                            <NavItem
                                className={styles.option}
                                text={'Payment methods'}
                                isActive={location.pathname.startsWith(PATH.PAYMENT_METHOD)}
                                isShown={hasPermissionOrAdmin(authUser, PERMISSION.PAYMENT_METHODS_VIEW)}
                                to={PATH.PAYMENT_METHODS}
                            />
                            <NavItem
                                className={styles.option}
                                text={'Proxies'}
                                isActive={
                                    location.pathname.startsWith(PATH.PROXY) ||
                                    location.pathname.startsWith(PATH.PROXIES)
                                }
                                isShown={hasPermissionOrAdmin(authUser, PERMISSION.PROXIES_VIEW)}
                                to={PATH.PROXIES}
                            />
                            <NavItem
                                className={styles.option}
                                text={'Agent statistics'}
                                isActive={location.pathname.startsWith(PATH.STATISTICS_AGENT)}
                                isShown={hasPermissionOrAdmin(authUser, PERMISSION.AGENTS_STATISTICS_VIEW)}
                                to={PATH.STATISTICS_AGENT}
                            />
                            <NavItem
                                className={styles.option}
                                text={'Users'}
                                isActive={location.pathname.startsWith(PATH.USER)}
                                isShown={authUser?.role === ROLE.SUPER_ADMIN}
                                to={PATH.USERS}
                            />
                            <NavItem
                                className={styles.option}
                                text={'Settings'}
                                isActive={location.pathname.startsWith(PATH.ADMIN_ACCOUNT_CONVERSION_SETTING)}
                                isShown={hasPermissionsOrAdmin(authUser, [PERMISSION.SETTINGS_CONVERSION_VIEW])}
                                to={PATH.ADMIN_ACCOUNT_CONVERSION_SETTING}
                            />
                            <NavItem
                                className={styles.option}
                                text={'Admin'}
                                isActive={
                                    location.pathname.startsWith(PATH.ADMIN_PANEL) &&
                                    !location.pathname.startsWith(PATH.ADMIN_ACCOUNT_CONVERSION_SETTING)
                                }
                                isShown={
                                    hasPermissionsOrAdmin(authUser, [
                                        PERMISSION.SETTINGS_CONVERSION_VIEW,
                                        PERMISSION.SETTINGS_PREPAY_VIEW,
                                        PERMISSION.SETTINGS_ANTI_FRAUD_VIEW,
                                    ])
                                }
                                to={PATH.ADMIN_PANEL}
                            />
                        </div>
                    }
                    <div
                        className={styles.option}
                        onClick={onLogout}
                    >
                        Log out
                    </div>
                </div>
            }
        </div>
    );
};

export default AuthInfo;
